import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import NewsletterSignUp from '../../common/Newsletter-sign';
import heroImage from '../../../assets/christmas25/grotto.webp';
import { useBlackFriday } from '../../../contexts/BlackFridayContext';

const Page = styled.div`
  min-height: 100vh;
  padding-top: 120px;
  background-color: ${props => props.theme.colors.offWhite};
`;

const EventHero = styled.div`
  width: 100%;
  height: 420px;
  background-image: ${props => `linear-gradient(to bottom, rgba(0,0,0,0.25), rgba(0,0,0,0.65)), url(${props.image})`};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  position: relative;

  @media (max-width: 1024px) { height: 420px; }
  @media (max-width: 768px) { height: 340px; }
`;

const HeroContent = styled.div`
  width: 100%;
  max-width: 1400px;
  padding: 3rem 2rem;
  color: white;
`;

const Badge = styled.span`
  display: inline-block;
  background: ${props => props.theme.colors.accent};
  color: white;
  padding: 0.4rem 0.8rem;
  border-radius: 999px;
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
`;

const Title = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: 3rem;
  font-weight: 700;
  margin: 0.25rem 0 0.5rem;
  line-height: 1.2;
  text-shadow: 0 2px 6px rgba(0,0,0,0.35);
`;

const Strapline = styled.p`
  font-size: 1.25rem;
  margin: 0;
  opacity: 0.95;
`;

const Content = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 1.5rem auto 0;
  padding: 0 2rem 3rem;
`;

const OptionsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-top: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const OptionCard = styled.div.attrs({ className: 'bf-surface' })`
  background: white;
  border-radius: 12px;
  padding: 2.5rem;
  box-shadow: 0 10px 30px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
  z-index: 1;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0,0,0,0.12);
  }
`;

const OptionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1rem;
  position: relative;
  padding-bottom: 0.5rem;

  &::after {
    content: '';
    position: absolute;
    left: 0; bottom: 0;
    width: 80px; height: 3px;
    background: ${props => props.theme.colors.accent};
  }
`;

const OptionDescription = styled.div`
  font-size: 1.1rem;
  line-height: 1.8;
  color: #333;
  margin-bottom: 1.5rem;
  flex-grow: 1;

  p {
    margin: 0 0 1rem;
  }

  ul {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
  }

  li {
    margin: 0.5rem 0;
  }
`;

const LocationBadge = styled.div`
  display: inline-block;
  background: rgba(157, 109, 73, 0.1);
  border: 1px solid rgba(157, 109, 73, 0.3);
  border-radius: 6px;
  padding: 0.5rem 1rem;
  margin-bottom: 1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  font-size: 0.95rem;
`;

const CTAButton = styled.a.attrs({ className: 'cta-button' })`
  display: inline-block;
  background: ${props => props.theme.colors.accent};
  color: white;
  padding: 0.9rem 1.5rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 700;
  margin-top: auto;
  text-align: center;
  transition: all 0.2s ease;
  
  &:hover {
    opacity: 0.95;
    transform: translateY(-2px);
  }
`;

const Notice = styled.div.attrs({ className: 'bf-surface' })`
  background: rgba(208, 56, 45, 0.12);
  border: 1px solid rgba(208, 56, 45, 0.4);
  border-radius: 8px;
  padding: 1rem 1.25rem;
  margin: 2rem 0;
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
  text-align: center;
`;

const SantasGrottoOptions = () => {
  const { isActive: isBlackFriday } = useBlackFriday();
  const pageClassName = isBlackFriday ? 'christmas-page christmas-page--black-friday' : 'christmas-page';
  const meta = {
    title: "Santa's Grotto Options - Belvoir Castle 2025 | Christmas Events",
    description: "Visit Santa's Grotto at Belvoir Retail Village this Christmas. Can do on its own or pair with Castle entry. Book now!",
    canonical: 'https://www.belvoircastle.com/christmas/santas-grotto-options'
  };

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: "Santa's Grotto Options at Belvoir Castle Christmas 2025",
    description: meta.description,
    url: meta.canonical
  };

  return (
    <Page className={pageClassName}>
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <meta name="keywords" content="Santa's Grotto, Christmas activities Leicestershire, Christmas family events, Belvoir Castle Christmas, Christmas day out, Santa visit, Christmas grotto, Belvoir Retail Village, Christmas at the Castle" />
        <meta name="author" content="Belvoir Castle" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <link rel="canonical" href={meta.canonical} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:image" content="https://www.belvoircastle.com/christmas25/grotto.webp" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={meta.canonical} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content="https://www.belvoircastle.com/christmas25/grotto.webp" />
        
        {/* Structured Data */}
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
        
        {/* Breadcrumb Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://www.belvoircastle.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Christmas 2025",
                "item": "https://www.belvoircastle.com/christmas"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": "Santa's Grotto Options",
                "item": meta.canonical
              }
            ]
          })}
        </script>
      </Helmet>

      <EventHero image={heroImage}>
        <HeroContent>
          <Badge>Choose Your Christmas Experience</Badge>
          <Title>Santa's Grotto</Title>
          <Strapline>Visit Santa's Grotto at Belvoir Retail Village</Strapline>
        </HeroContent>
      </EventHero>

      <Content>
        <OptionsContainer>
          <OptionCard>
            <OptionTitle>Santa's Grotto at Belvoir Retail Village</OptionTitle>
            <LocationBadge>📍 At Belvoir Retail Village (5 min walk from Castle)</LocationBadge>
            <OptionDescription>
              <p><strong>Santa's Grotto at the Belvoir Retail Village</strong></p>
              <p>Visit Santa in his magical grotto at Belvoir Retail Village. This flexible option allows you to:</p>
              <ul>
                <li>Visit Santa's Grotto on its own at the Retail Village</li>
                <li>Or pair with a Castle Entry ticket to make it a whole day out</li>
                <li>Available 20th-23rd December 2025</li>
                <li>Private family visit with Santa and a special present for your child</li>
                <li>Photo opportunities in a beautifully decorated festive setting</li>
                <li>Explore the Retail Village shops and dining options</li>
              </ul>
              <p><strong>Perfect for:</strong> Families who want flexibility - visit Santa only, or combine with Castle entry for a complete Christmas experience.</p>
            </OptionDescription>
            <CTAButton href="https://www.belvoircastle.com/christmas/santas-grotto">
              Book Now
            </CTAButton>
          </OptionCard>
        </OptionsContainer>
      </Content>

      <div style={{ margin: '3rem 0' }}>
        <NewsletterSignUp tag="Christmas 2025" />
      </div>
    </Page>
  );
};

export default SantasGrottoOptions;

